import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FirebaseError } from 'firebase/app';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import {
  InviteRequestActionPreview,
  InviteRequestActionResult,
  confirmInviteRequestAction,
  previewInviteRequestAction,
} from '../../services/adminInvites';
import { signUpErrorMessage } from '../../services/betaSignUp';

const INVALID_MESSAGE = 'This link is not valid.';
const ALREADY_HANDLED_MESSAGE = 'This request was already handled.';
const EXPIRED_MESSAGE = 'This link has expired. Answer the request from /admin.';

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; preview: InviteRequestActionPreview };

function confirmErrorMessage(error: unknown): string {
  if (error instanceof FirebaseError && error.code === 'functions/permission-denied') return INVALID_MESSAGE;
  return signUpErrorMessage(error);
}

function successMessage(result: InviteRequestActionResult): string {
  if (result.emailStatus === 'failed') {
    return "The request was answered, but the email didn't send. Open /admin to copy the link.";
  }
  if (result.emailStatus === 'skipped') {
    return result.outcome === 'sent'
      ? `${result.requesterName}'s invite code was created, but no email was sent because email sending isn't set up. Open /admin to copy the sign-up link.`
      : `${result.requesterName}'s request was declined, but no email was sent because email sending isn't set up.`;
  }
  return result.outcome === 'sent'
    ? `Done. ${result.requesterName} has been sent their invite.`
    : `Done. ${result.requesterName} has been sent their reply.`;
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-base-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow p-6 space-y-4">{children}</div>
    </div>
  );
}

function MessageCard({ message, showAdminLink }: { message: string; showAdminLink: boolean }) {
  return (
    <PageShell>
      <div className="text-center space-y-3">
        <AlertTriangle className="w-12 h-12 text-warning mx-auto" />
        <p className="text-gray-900 dark:text-white font-medium">{message}</p>
        {showAdminLink && (
          <Link to="/admin" className="inline-block text-primary hover:text-primary-hover font-medium">
            Open admin
          </Link>
        )}
      </div>
    </PageShell>
  );
}

export function InviteRequestActionPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [loadState, setLoadState] = useState<LoadState>(token ? { phase: 'loading' } : { phase: 'error', message: INVALID_MESSAGE });
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState('');
  const [result, setResult] = useState<InviteRequestActionResult | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    previewInviteRequestAction(token)
      .then((preview) => {
        if (!cancelled) setLoadState({ phase: 'ready', preview });
      })
      .catch((error: unknown) => {
        console.error('Preview invite request action failed', error);
        if (cancelled) return;
        const invalid = error instanceof FirebaseError && error.code === 'functions/invalid-argument';
        setLoadState({ phase: 'error', message: invalid ? INVALID_MESSAGE : 'Something went wrong. Please try again.' });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleConfirm = async () => {
    if (!token) return;
    setConfirming(true);
    setConfirmError('');
    try {
      setResult(await confirmInviteRequestAction(token));
    } catch (error) {
      console.error('Confirm invite request action failed', error);
      setConfirmError(confirmErrorMessage(error));
    } finally {
      setConfirming(false);
    }
  };

  if (loadState.phase === 'loading') {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-primary" />
          <p className="text-base-content/60">Checking link...</p>
        </div>
      </div>
    );
  }

  if (loadState.phase === 'error') return <MessageCard message={loadState.message} showAdminLink={false} />;

  const { preview } = loadState;
  if (preview.state === 'invalid') return <MessageCard message={INVALID_MESSAGE} showAdminLink={false} />;
  if (preview.state === 'used' || preview.state === 'already-handled') {
    return <MessageCard message={ALREADY_HANDLED_MESSAGE} showAdminLink />;
  }
  if (preview.state === 'expired') return <MessageCard message={EXPIRED_MESSAGE} showAdminLink />;

  if (result) {
    return (
      <PageShell>
        <div className="text-center space-y-3">
          {result.emailStatus !== 'sent' ? (
            <AlertTriangle className="w-12 h-12 text-warning mx-auto" />
          ) : (
            <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" />
          )}
          <p className="text-gray-900 dark:text-white font-medium">{successMessage(result)}</p>
          <Link to="/admin" className="inline-block text-primary hover:text-primary-hover font-medium">
            Open admin
          </Link>
        </div>
      </PageShell>
    );
  }

  const requesterName = preview.requesterName ?? '';
  const confirmLabel =
    preview.kind === 'decline' ? `Send the 'not right now' email to ${requesterName}` : `Send invite code to ${requesterName}`;

  return (
    <PageShell>
      <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Invite code request</h1>
      <div className="space-y-1">
        <p className="font-medium text-gray-900 dark:text-white">{requesterName}</p>
        <p className="text-sm text-gray-600 dark:text-gray-400">{preview.requesterEmail}</p>
        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{preview.requesterNote}</p>
      </div>
      {confirmError && (
        <div role="alert" className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
          {confirmError}
        </div>
      )}
      <button type="button" className="btn btn-primary w-full" disabled={confirming} onClick={handleConfirm}>
        {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : confirmLabel}
      </button>
      <div className="text-center">
        <Link to="/admin" className="text-sm text-gray-500 dark:text-gray-400 hover:underline">
          Cancel
        </Link>
      </div>
    </PageShell>
  );
}
